import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Layout, Button, Form, PageHeader, Checkbox, Spin, message, Row, Col } from 'antd'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'

import { useStores } from '../../../Store/Store'
import { PERSONAL_DATA_POLICY_PAGE, ELECTRONIC_SIGNATURE_POLICY_PAGE, PROFILE_MAIN_PAGE } from '../../../routes'
import './PolicyScreenStyle.css'
import {
  useGetPersonalDataPolicyQuery,
  useGetElectronicSignaturePolicyQuery, useMutatePrivacyPolicyAccept, useMutateElectronicSignaturePolicyAccept,
  useMutateAcceptOffer,
  useGetOffer
} from '../../../api/react-query/partners'
import { offerScreen, policyScreen } from '../../../translates'
import { formLayout } from '../../../Constants'
import { AppConfig } from '../../../AppConfig'
// import { useRequiredOffers } from '../../../hooks'
import { digestMessage } from '../../../helper'

const { Content } = Layout

const PolicyScreen = () => {
  const store = useStores()
  const [personalDataPolicy, setPersonalDataPolicy] = useState({})
  const [electronicSignaturePolicy, setElectronicSignaturePolicy] = useState({})

  const roleCode = useMemo(() => {
    return store?.partnerProfile?.profile?.personalData?.roleCode
  }, [store?.partnerProfile?.profile])

  const isSzOperator = useMemo(() => {
    return store?.partnerProfile?.profile?.personalData?.roleCode === AppConfig.partnerRoles.szOperator
  }, [store?.partnerProfile?.profile?.personalData?.roleCode])

  const isSzMentor = useMemo(() => {
    return store?.partnerProfile?.profile?.personalData?.roleCode === AppConfig.partnerRoles.szMentor
  }, [store?.partnerProfile?.profile?.personalData?.roleCode])

  const isSzResearcher = useMemo(() => {
    return store?.partnerProfile?.profile?.personalData?.roleCode === AppConfig.partnerRoles.researcher
  }, [store?.partnerProfile?.profile?.personalData?.roleCode])

  const isSelfEmployedPath = useMemo(() => {
    return AppConfig.partnerAdditionalRoles.includes(roleCode)
  }, [roleCode])

  const { data: dataRoleOffer, isError: isErrorRoleOffer } = useGetOffer(roleCode, {
    enabled: isSzMentor || isSzResearcher || isSzOperator || !isSelfEmployedPath,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (isErrorRoleOffer) {
      message.error(offerScreen.errors.offer)
    }
  }, [isErrorRoleOffer])

  const {
    mutate: acceptRoleOffer,
    data: dataAcceptRoleOffer,
    isLoading: isLoadingAcceptRoleOffer,
    isSuccess: isSuccessAcceptRoleOffer,
    isError: isErrorAcceptRoleOffer
  } = useMutateAcceptOffer()

  useEffect(() => {
    if (isSuccessAcceptRoleOffer) {
      message.success(`${offerScreen.successTextStart} ${offerScreen.offerParts.roleOffer} ${offerScreen.successTextEnd}`)
      store?.partnerProfile?.setPartnerProfile({
        profile: dataAcceptRoleOffer?.profile,
        ...(store?.partnerProfile?.id ? { id: store?.partnerProfile?.id } : {})
      })
    }
  }, [dataAcceptRoleOffer, isSuccessAcceptRoleOffer])

  const {
    data: dataPersonalDataPolicy,
    isError: isErrorPersonalDataPolicy,
    isSuccess: isSuccessPersonalDataPolicy,
    isLoading: isLoadingPersonalDataPolicy
  } = useGetPersonalDataPolicyQuery()

  useEffect(() => {
    if (!dataPersonalDataPolicy?.isSuccess) return
    if (isSuccessPersonalDataPolicy) {
      setPersonalDataPolicy(dataPersonalDataPolicy.agreement)
    }
  }, [dataPersonalDataPolicy, isSuccessPersonalDataPolicy])

  const {
    data: dataElectronicSignaturePolicy,
    isError: isErrorElectronicSignaturePolicy,
    isSuccess: isSuccessElectronicSignaturePolicy,
    isLoading: isLoadingElectronicSignaturePolicy
  } = useGetElectronicSignaturePolicyQuery()

  useEffect(() => {
    if (!dataElectronicSignaturePolicy?.isSuccess) return
    if (isSuccessElectronicSignaturePolicy) {
      setElectronicSignaturePolicy(dataElectronicSignaturePolicy.agreement)
    }
  }, [dataElectronicSignaturePolicy, isSuccessElectronicSignaturePolicy])

  const {
    // error: errorPrivacyPolicyAccept,
    data: dataPrivacyPolicyAccept,
    mutateAsync: mutatePrivacyPolicyAccept,
    isError: isErrorPrivacyPolicyAccept,
    isLoading: isLoadingPrivacyPolicyAccept,
    isSuccess: isSuccessPrivacyPolicyAccept
  } = useMutatePrivacyPolicyAccept()

  useEffect(() => {
    if (isSuccessPrivacyPolicyAccept) {
      store?.partnerProfile?.setPartnerProfile({
        profile: dataPrivacyPolicyAccept?.profile,
        ...(store?.partnerProfile?.id ? { id: store?.partnerProfile?.id } : {})
      })
      message.success(policyScreen.personalDataPolicy.success)
    }
  }, [isSuccessPrivacyPolicyAccept, dataPrivacyPolicyAccept])

  const {
    // error: errorElectronicSignaturePolicyAccept,
    data: dataElectronicSignaturePolicyAccept,
    mutateAsync: mutateElectronicSignaturePolicyyAccept,
    isError: isErrorElectronicSignaturePolicyAccept,
    isLoading: isLoadingElectronicSignaturePolicyAccept,
    isSuccess: isSuccessElectronicSignaturePolicyAccept
  } = useMutateElectronicSignaturePolicyAccept()

  useEffect(() => {
    if (isSuccessElectronicSignaturePolicyAccept) {
      message.success(policyScreen.electronicSignaturePolicy.success)
      store?.partnerProfile?.setPartnerProfile({
        profile: dataElectronicSignaturePolicyAccept?.profile,
        ...(store?.partnerProfile?.id ? { id: store?.partnerProfile?.id } : {})
      })
    }
  }, [isSuccessElectronicSignaturePolicyAccept, dataElectronicSignaturePolicyAccept])

  /**
   * Errors from api requests
   */
  useEffect(() => {
    if (isErrorElectronicSignaturePolicy || isErrorPersonalDataPolicy) {
      message.error(policyScreen.errors.agreement)
    }
    if (isErrorPrivacyPolicyAccept) {
      message.error(policyScreen.errors.agreementAccept)
    }
    if (isErrorElectronicSignaturePolicyAccept) {
      message.error(policyScreen.errors.agreementAccept)
    }
    if (isErrorAcceptRoleOffer) {
      message.error(offerScreen.errors.offer)
    }
  }, [
    isErrorPrivacyPolicyAccept,
    isErrorPersonalDataPolicy,
    isErrorElectronicSignaturePolicyAccept,
    isErrorElectronicSignaturePolicy,
    isErrorAcceptRoleOffer
  ])

  const onFinish = async values => {
    if (((store?.partnerProfile?.profile?.personalDataPolicy?.isValid || values.acceptPersonalDataPolicy) &&
      (store?.partnerProfile?.profile?.electronicSignaturePolicy?.isValid || values.acceptElectronicSignaturePolicy)) ||
      ((isSzMentor || isSzResearcher || isSzOperator || !isSelfEmployedPath) && (store?.partnerProfile?.profile?.electronicSignaturePolicy?.isValid || values.acceptElectronicSignaturePolicy))) {
      try {
        if (values.acceptPersonalDataPolicy || isSzMentor || isSzResearcher || isSzOperator || !isSelfEmployedPath) {
          await mutatePrivacyPolicyAccept({
            agreementId: personalDataPolicy.id
          })
        }
        if (values.acceptElectronicSignaturePolicy) {
          await mutateElectronicSignaturePolicyyAccept({
            agreementId: electronicSignaturePolicy.id
          })
        }
        if (isSzMentor || isSzResearcher || isSzOperator || !isSelfEmployedPath) {
          const offerHash = await digestMessage(dataRoleOffer.offer?.subject + dataRoleOffer.offer?.body + dataRoleOffer.offer?.agreementTerm)
          await acceptRoleOffer({
            offerId: dataRoleOffer.offer?.id,
            offerHash,
            validityFrom: dataRoleOffer.validityFrom,
            validityTo: dataRoleOffer.validityTo
          })
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  const myRef = useRef(null)
  useEffect(() => {
    if (myRef.current) {
      document.body.scrollIntoView({ behavior: 'smooth' })
    }
  }, [myRef])
  const onFinishFailed = errorInfo => {
    console.log('Failed:', errorInfo)
  }

  const [form] = Form.useForm()
  const [requiredMark, setRequiredMarkType] = useState('required')

  const onRequiredTypeChange = ({ requiredMark }) => {
    setRequiredMarkType(requiredMark)
  }

  const docParams = useMemo(() => {
    return AppConfig.partnerAdditionalRoles.includes(roleCode)
      ? `?role=${roleCode}`
      : ''
  }, [roleCode])

  return (
    <div ref={myRef} className='height-inherit'>
      <Spin spinning={isLoadingElectronicSignaturePolicy || isLoadingPersonalDataPolicy} size='large'>
        <Content className='PolicyScreen'>
          <Form
            {...formLayout}
            name='basic'
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            layout='vertical'
            initialValues={{ requiredMark, remember: true }}
            onValuesChange={onRequiredTypeChange}
            requiredMark={requiredMark}
          >
            <Row justify='space-between' className='flex-column'>
              <Col style={{ width: '100%' }}>
                <PageHeader title={policyScreen.title} />

                {!store?.partnerProfile?.profile?.electronicSignaturePolicy?.isValid &&
                  <Form.Item
                    name='acceptElectronicSignaturePolicy'
                    valuePropName='checked'
                    className='align-start'
                    rules={[{
                      required: true,
                      transform: value => (value || undefined),
                      type: 'boolean',
                      message: policyScreen.agreementText
                    }]}
                  >
                    <Checkbox>
                      <Link to={ELECTRONIC_SIGNATURE_POLICY_PAGE}>{policyScreen.electronicSignaturePolicy.placeholder}</Link>
                    </Checkbox>
                  </Form.Item>}

                {!store?.partnerProfile?.profile?.personalDataPolicy?.isValid && (
                  (isSzMentor || isSzResearcher || isSzOperator || !isSelfEmployedPath)
                    ? (
                      <>
                        Продолжая,{' '}
                        <Link to={`${PROFILE_MAIN_PAGE}?action=${AppConfig.formScreenActions.offer}${roleCode ? `&role=${roleCode}` : ''}`}>
                          я согласен с Офертой.
                        </Link>
                        Ознакомлен с{' '}
                        <Link
                          to={`${PERSONAL_DATA_POLICY_PAGE}${docParams}`}
                        >
                          Политикой конфиденциальности
                        </Link>
                      </>
                    )
                    : (
                      <Form.Item
                        name='acceptPersonalDataPolicy'
                        valuePropName='checked'
                        className='align-start'
                        rules={[{
                          required: true,
                          transform: value => (value || undefined),
                          type: 'boolean',
                          message: policyScreen.agreementText
                        }]}
                      >
                        <Checkbox>
                          <Link to={`${PERSONAL_DATA_POLICY_PAGE}${docParams}`}>
                            {isSzOperator
                              ? policyScreen.personalDataPolicy.placeholderSzOperator
                              : (isSzMentor || isSzResearcher || isSzOperator)
                                ? policyScreen.personalDataPolicy.placeholderSzMentor
                                : policyScreen.personalDataPolicy.placeholder}
                          </Link>
                        </Checkbox>
                      </Form.Item>
                    )
                )}
              </Col>
              <Col>
                <Form.Item>
                  <Button
                    block
                    size='large'
                    type='primary'
                    htmlType='submit'
                    className='continue-button'
                    loading={isLoadingPrivacyPolicyAccept || isLoadingElectronicSignaturePolicyAccept || isLoadingAcceptRoleOffer}
                  >
                    {policyScreen.button.continue}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Content>
      </Spin>
    </div>
  )
}
export default observer(PolicyScreen)
